<script setup lang="ts">
import { computed, ref } from "vue"
import type { JobOffer } from "@/model/backend/job-offer"
import { $date } from "@/vf"

const props = defineProps<{
    offer: JobOffer
    expanded?: boolean
}>()

const detailsRef = ref<HTMLDetailsElement | null>(null)
function toggleDetails(e: Event) {
    e.preventDefault()
    if (detailsRef.value) {
        detailsRef.value.open = !detailsRef.value.open
    }
}

function formatEmploymentType(et?: string) {
    if (et === "FullTime") return "Vollzeit"
    if (et === "PartTime") return "Teilzeit"
    return et ?? ""
}

const mailLink = computed(
    () =>
        `mailto:jobs@cann24.de?subject=Bewerbung%20auf%20die%20Position%20"${encodeURIComponent(props.offer.title!)}"`,
)
</script>

<template>
    <details ref="detailsRef" class="job-item" :open="props.expanded" @click="toggleDetails">
        <summary class="job-summary" @keydown.enter.prevent="toggleDetails" @keydown.space.prevent="toggleDetails">
            <div class="job-summary-main">
                <h3 class="job-title mb-0">{{ props.offer.title }}</h3>
                <div class="job-meta">
                    <span v-if="props.offer.location" class="badge text-body-secondary me-2 pl-0 hide-mobile">{{
                        props.offer.location
                    }}</span>
                    <span v-if="props.offer.employmentType" class="badge text-body-secondary me-2 hide-mobile">{{
                        formatEmploymentType(props.offer.employmentType as any)
                    }}</span>
                    <span v-if="props.offer.applicationDeadline" class="badge text-body-secondary"
                        >Bewerbungsschluss: {{ $date(props.offer.applicationDeadline) }}</span
                    >
                </div>
            </div>
            <span class="job-caret" aria-hidden="true"></span>
        </summary>
        <div class="job-details">
            <div v-if="props.offer.description" class="mb-3">
                <p class="mb-0" v-html="props.offer.description"></p>
            </div>
            <div v-if="props.offer.requirements" class="mb-3">
                <p class="section-title">Anforderungen</p>
                <p class="mb-0" v-html="props.offer.requirements"></p>
            </div>
            <div v-if="props.offer.benefits" class="mb-3">
                <p class="section-title">Was wir dir bieten können</p>
                <p class="mb-0" v-html="props.offer.benefits"></p>
            </div>
            <div class="mb-3">
                <p class="section-title">Erforderliche Unterlagen</p>
                <p class="mb-0">
                    Anschreiben (einschließlich Motivation), Lebenslauf mit Lichtbild, Nachweise über Qualifikationen
                    (z.B. Berufserlaubnis, Urkunden, Zusatzqualifikationen, Fortbildungen)
                </p>
            </div>

            <dl class="row small mb-0 mt-5">
                <template v-if="props.offer.location">
                    <dt class="col-sm-4">Ort</dt>
                    <dd class="col-sm-8">{{ props.offer.location }}</dd>
                </template>
                <template v-if="props.offer.employmentType">
                    <dt class="col-sm-4">Anstellungsart</dt>
                    <dd class="col-sm-8">{{ formatEmploymentType(props.offer.employmentType as any) }}</dd>
                </template>
                <template v-if="props.offer.applicationDeadline">
                    <dt class="col-sm-4">Bewerbungsschluss</dt>
                    <dd class="col-sm-8">{{ $date(props.offer.applicationDeadline) }}</dd>
                </template>
                <template v-if="props.offer.beginning">
                    <dt class="col-sm-4">Beginn</dt>
                    <dd class="col-sm-8">{{ $date(props.offer.beginning) }}</dd>
                </template>
                <template v-if="props.offer.workHours">
                    <dt class="col-sm-4">Arbeitszeiten</dt>
                    <dd class="col-sm-8">{{ props.offer.workHours }}</dd>
                </template>
                <template v-if="props.offer.hoursPerWeek">
                    <dt class="col-sm-4">Wochenstunden</dt>
                    <dd class="col-sm-8">{{ props.offer.hoursPerWeek }}</dd>
                </template>
                <template v-if="props.offer.salary">
                    <dt class="col-sm-4">Vergütung</dt>
                    <dd class="col-sm-8">{{ props.offer.salary }}</dd>
                </template>
                <template v-if="props.offer.contactPerson">
                    <dt class="col-sm-4">Kontakt</dt>
                    <dd class="col-sm-8">{{ props.offer.contactPerson }}</dd>
                </template>
            </dl>
            <div class="mt-3">
                <a class="btn btn-primary apply-button" :href="mailLink" aria-label="Per E-Mail bewerben" @click.stop>
                    Jetzt bewerben
                </a>
            </div>
        </div>
    </details>
</template>

<style scoped>
.job-item {
    position: relative;
    border: 1px solid #e5e5ea;
    border-radius: 12px;
    background: #fff;
    overflow: hidden;
    transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.job-item::before {
    content: "";
    position: absolute;
    inset: 0 0 0 0;
    border-left: 4px solid transparent;
    transition: border-color 0.2s ease;
}
.job-item:hover {
    border-color: #d5d7dc;
    box-shadow: 0 1px 12px rgba(0, 0, 0, 0.04);
}
.job-item[open] {
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
}
.job-item:hover::before,
.job-item[open]::before {
    border-left-color: var(--bs-primary, #20c997);
}

.job-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.1rem 1.25rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
}
.job-summary:hover {
    background-color: #fafbfc;
}
.job-summary:focus-visible {
    outline: 2px solid var(--bs-primary, #20c997);
    outline-offset: 2px;
}
.job-summary::-webkit-details-marker {
    display: none;
}

.job-summary-main {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}
.job-title {
    font-weight: 600;
    line-height: 1.3;
}
.job-meta {
    font-size: 0.9rem;
    color: black;
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}
.job-meta .badge {
    font-size: 0.8rem;
    font-weight: unset;
}

.job-caret {
    width: 10px;
    height: 10px;
    border-right: 2px solid #6c757d;
    border-bottom: 2px solid #6c757d;
    transform: rotate(-45deg);
    transition: transform 0.2s ease;
    flex: 0 0 auto;
}
.job-item[open] .job-caret {
    transform: rotate(45deg);
}

.job-details {
    border-top: 1px solid #eee;
    padding: 1rem 1.25rem;
}
.section-title {
    margin: 0 0 0.35rem;
    font-size: 0.8rem;
    color: black;
    font-weight: 600;
}
.job-details p {
    line-height: 1.55;
}

/* hide certain badges on mobile */
@media (max-width: 767.98px) {
    .hide-mobile {
        display: none !important;
    }
}

/* description lists -> responsive grid */
.row.small {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.25rem 0.75rem;
}
.row.small dt {
    color: black;
    font-weight: 500;
}
.row.small dd {
    margin: 0 0 0.5rem;
}

.apply-button {
    z-index: 10;
    position: relative;
}
@media (min-width: 768px) {
    .row.small {
        grid-template-columns: 220px 1fr;
        align-items: start;
    }
}
</style>
